<script setup lang="ts">
import '@aab/sc-aab-icon';
import { comparePeriods, formatPercentage, getFixedPeriodInYears, isDefined } from '@/utils';
import { computed, onMounted, reactive } from 'vue';
import { InterestPeriodType, RiskCategoryType } from '@shared/constants';
import type { Period, PeriodRate } from '@shared/models';
import { sy_arrow_chevron_down as syArrowChevronDown, sy_arrow_chevron_up as syArrowChevronUp } from '@aab/sc-aab-icon-set';
import IText from '@/components/IText/IText.vue';
import { RateType } from '@shared/models';
import ScAnalyticsApi from '@aab/sc-analytics-api';
import { showSecondaryHeader } from './OffsetHelper';
import { translate } from '@/i18n';
import type { TranslateResult } from 'vue-i18n';
import { useI18nStore } from '@/stores/i18n';

const props = withDefaults(
  defineProps<{
    readonly interestPeriods: ReadonlyArray<Period>;
    readonly shownInterestPeriods: ReadonlyArray<number>;
    readonly headerLabels: ReadonlyArray<string>;
    readonly usedRateCategories: ReadonlyArray<RateType | RiskCategoryType>;
  }>(),
  { interestPeriods: () => [], shownInterestPeriods: () => [], headerLabels: () => [], usedRateCategories: () => [] }
);

const state = reactive({
  interestPeriodsFilter: props.shownInterestPeriods as ReadonlyArray<any> | undefined,
  iframeSrcUrl: undefined as any
});

onMounted(() => {
  getSecondaryTableHeader();
  window.parent.addEventListener('scroll', () => showSecondaryHeader((url: string) => (state.iframeSrcUrl = url)));
  window.parent.addEventListener('resize', () => {
    getSecondaryTableHeader();
    showSecondaryHeader((url: string) => (state.iframeSrcUrl = url));
  });
});

const sortedInterestPeriods = computed((): ReadonlyArray<Period> => {
  return [...props.interestPeriods].sort(comparePeriods);
});

const interestNotInactive = computed((): ReadonlyArray<Period> => {
  return sortedInterestPeriods.value.filter((interestPeriod) => interestPeriod.inactive === false);
});

const filteredInterestPeriods = computed((): ReadonlyArray<Period> => {
  if (isDefined(state.interestPeriodsFilter) && state.interestPeriodsFilter.length > 0) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return interestNotInactive.value.filter((period: Period) => state.interestPeriodsFilter!.indexOf(getFixedPeriodInYears(period)) > -1);
  }
  return sortedInterestPeriods.value;
});

function showAllInterestPeriods(): void {
  ScAnalyticsApi.eventTrack({
    eventName: 'click',
    eventCategory: 'interest rates table',
    eventAction: 'irt: show all terms clicked'
  });
  state.interestPeriodsFilter = undefined;
  const primaryTable = document.getElementById('primaryTable');
  if (primaryTable) primaryTable.focus();
}

function showDefaultInterestPeriods(): void {
  ScAnalyticsApi.eventTrack({
    eventName: 'click',
    eventCategory: 'interest rates table',
    eventAction: 'irt: show fewer terms clicked'
  });

  state.interestPeriodsFilter = props.shownInterestPeriods;
}

function getValue(expectedRateCategory: RateType | RiskCategoryType, interestRates: ReadonlyArray<PeriodRate>): string {
  const matchedInterestRate = interestRates.filter(({ ltv, type }) => (ltv || type) === expectedRateCategory);
  return matchedInterestRate.length > 0 ? matchedInterestRate[0].value : translate('text:notApplicable');
}

function getConsiderationPeriodItext(fixedPeriodInYears: number): TranslateResult {
  return Number(fixedPeriodInYears) !== 2 ? translate('iText:considerationPeriod') : translate('iText:considerationPeriodTwoYears');
}

function getSecondaryTableHeader(): void {
  const primaryHeader = document.getElementById('primaryHeader');
  if (primaryHeader) {
    const primaryTableHeadClone = primaryHeader.cloneNode(true);
    const secondaryTable = document.getElementById('secondaryTable');
    if (secondaryTable) {
      if (secondaryTable.hasChildNodes()) {
        if (secondaryTable.firstChild) secondaryTable.removeChild(secondaryTable.firstChild);
      }
      secondaryTable.appendChild(primaryTableHeadClone);
    }
  }
}

function isScrollable(): boolean {
  return props.headerLabels.length > 2;
}
</script>

<template>
  <div class="interest-rates-table mt-1">
    <div class="align-header" :class="{ 'scrollable-table': isScrollable(), 'table-overflow': !isScrollable() }">
      <div class="white-gradient">
        <table id="primaryTable" class="table mb-0" :tabindex="state.interestPeriodsFilter ? -1 : 0">
          <thead v-if="headerLabels && headerLabels.length > 0" id="primaryHeader">
            <tr class="w-100">
              <th class="interest-rates-table__table-header -term remove-top-border text-nowrap add-right-border font-weight-normal w-15 text-heavy-green" :class="{ 'w-27-20': !isScrollable() }">
                {{ $t('label:term') }}
              </th>
              <th
                v-for="(header, index) in headerLabels"
                :key="index"
                class="interest-rates-table__table-header remove-top-border font-weight-normal w-15 text-heavy-green"
                :class="{ 'text-nowrap': isScrollable() }"
              >
                {{ header }}
              </th>
            </tr>
          </thead>
          <tbody v-if="filteredInterestPeriods && filteredInterestPeriods.length > 0">
            <tr v-for="(interestPeriod, index) in filteredInterestPeriods" :key="index">
              <td v-if="interestPeriod.type === InterestPeriodType.Variable" class="interest-rates-table__table-cell add-right-border w-15 text-nowrap font-weight-normal">
                {{ $t('text:variable') }}
              </td>
              <!-- inactive is true / Euribor: -->
              <td v-if="interestPeriod.inactive === true" class="interest-rates-table__table-cell add-right-border w-15 text-nowrap font-weight-normal text-gray">
                <div class="d-flex">
                  {{ interestPeriod.type === InterestPeriodType.Euribor ? $t('text:euribor') : $t('text:months', { duration: interestPeriod.duration }) }}
                  <IText class="itext-dropdown ml-1" :name="'euribor'" :with-offset="true">
                    <div class="text-black">
                      {{ interestPeriod.type === InterestPeriodType.Euribor ? $t('iText:euribor') : $t(`iText:months-${interestPeriod.duration}`) }}
                    </div>
                    <aab-link v-if="interestPeriod.type === InterestPeriodType.Euribor" target="_blank" :url="$t('linkUrl:euribor')" rel="noopener noreferrer">
                      {{ $t('linkText:euribor') }}
                    </aab-link>
                  </IText>
                </div>
              </td>
              <td
                v-if="interestPeriod.type === InterestPeriodType.Fixed && interestPeriod.inactive === false"
                class="interest-rates-table__table-cell add-right-border w-15 text-nowrap font-weight-normal"
              >
                <div class="d-flex">
                  {{ `${getFixedPeriodInYears(interestPeriod)} ${$t('text:year')}` }}
                  <IText
                    v-if="interestPeriod.reflectionPeriod && Number(interestPeriod.reflectionPeriod) !== 0"
                    class="ml-1 table-itext"
                    :name="`consideration period ${getFixedPeriodInYears(interestPeriod)}`"
                    :with-offset="true"
                  >
                    {{ getConsiderationPeriodItext(getFixedPeriodInYears(interestPeriod)) }}
                  </IText>
                </div>
              </td>
              <td
                v-for="(expectedRateCategory, idx) in usedRateCategories"
                :key="idx"
                class="interest-rates-table__table-cell w-15 text-nowrap font-weight-normal text-gray"
                :class="{ 'text-black': interestPeriod.inactive !== true }"
              >
                {{ formatPercentage(getValue(expectedRateCategory, interestPeriod.rates), useI18nStore().locale) }}
              </td>
            </tr>
          </tbody>
        </table>
        <table id="secondaryTable" class="table mb-0 d-none sticky-header" />
      </div>
    </div>
    <div v-if="shownInterestPeriods.length < interestPeriods.length">
      <button
        v-if="state.interestPeriodsFilter"
        id="table-extend-btn"
        type="button"
        :aria-label="$t('label:showAllTerms')"
        class="interest-rates-table__btn list-group-item extend-button text-heavy-green d-flex justify-content-center"
        tabindex="0"
        @click="showAllInterestPeriods()"
        @keydown.prevent.space="showAllInterestPeriods()"
        @keydown.enter="showAllInterestPeriods()"
      >
        <aab-icon :svg="syArrowChevronDown" size="3" />
        <span>{{ $t('label:showAllTerms') }}</span>
      </button>
      <button
        v-else
        type="button"
        :aria-label="$t('label:showFewerTerms')"
        class="interest-rates-table__btn list-group-item extend-button text-heavy-green d-flex justify-content-center"
        tabindex="0"
        @click="showDefaultInterestPeriods()"
        @keydown.prevent.space="showDefaultInterestPeriods()"
        @keydown.enter="showDefaultInterestPeriods()"
      >
        <aab-icon :svg="syArrowChevronUp" size="3" />
        <span>{{ $t('label:showFewerTerms') }}</span>
      </button>
    </div>
    <div v-else>
      <hr class="m-0" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use '@aab/sc-styles-colors/dist/scss/styles' as colors;
@use '@aab/sc-styles-spacing/dist/scss/styles' as spacing;
@use '@aab/sc-styles-utilities/dist/scss/styles' as utilities;
@use '~@aab/sc-styles-grid/dist/scss/styles.scss' as styles;

@include styles.row;
@include styles.col-1;

@include spacing.mt-1;
@include spacing.ml-1;
@include spacing.mt-1;
@include spacing.m-0;
@include spacing.mb-0;
@include spacing.m-0;
@include spacing.mb-0;

@include utilities.d-none;
@include utilities.d-flex;
@include utilities.justify-content-center;
@include utilities.text-nowrap;

.interest-rates-table {
  position: relative;
  font-family: 'Roboto', 'Helvetica Neue', 'Arial', 'sans-serif';
  line-height: 1.5;
  font-size: 1rem;

  @media (max-width: 425px) {
    font-size: 14px !important;
  }

  .table {
    width: 100%;
    color: colors.$n670;
  }

  &__table-header {
    padding: 0.75rem;
    vertical-align: bottom;
    border-bottom: 2px solid colors.$n75;

    &.-term {
      text-align: left;
    }
  }

  .text-heavy-green {
    color: colors.$g400 !important;
  }

  &__table-cell {
    padding: 0.75rem;
    vertical-align: top;
    border-top: 1px solid colors.$n75;
    text-align: center;

    &:first-of-type {
      text-align: left;
    }
  }

  .remove-top-border {
    border-top: 0;
  }

  .add-right-border {
    border-right: 2px solid colors.$n75;
  }

  .extend-button {
    border-left: 0;
    border-right: 0;
    background-color: colors.$gm55;
    padding-top: 12px;
    padding-bottom: 12px;
    cursor: pointer;
    border: none;
    outline: none;
    width: 100%;
    font-size: 16px;
    line-height: 24px;
    font-family: 'Roboto', 'Roboto Condensed', sans-serif;

    &:hover,
    &:focus {
      background-color: colors.$gm65;
    }
  }

  .list-group-item:last-child {
    margin-bottom: 0;
    border-radius: 0;
  }

  .w-15 {
    width: 15% !important;
  }

  .w-20 {
    width: 20% !important;
  }

  .w-27-20 {
    @media (max-width: 475px) {
      width: 27% !important;
    }
    @media (min-width: 476px) {
      width: 20% !important;
    }
  }

  .font-weight-normal {
    font-weight: 500 !important;
  }

  .text-gray {
    color: #666666 !important;
  }

  .text-black {
    color: #222 !important;
  }

  .sticky-header {
    position: fixed;
    background-color: white;
    @media (max-width: 410px) {
      display: none !important;
    }
  }

  .align-header {
    @media (min-width: 426px) and (max-width: 474px) {
      th:first-child,
      td:first-child {
        min-width: 110px !important;
      }
    }
    @media (min-width: 340px) and (max-width: 425px) {
      th:first-child,
      td:first-child {
        min-width: 104px !important;
      }
    }
  }

  .text-nowrap {
    @media (min-width: 475px) {
      white-space: nowrap !important;
    }
  }

  // mainly for overwaarde NL page
  .table-overflow {
    @media (max-width: 266px) {
      overflow-x: auto;
    }
  }

  .scrollable-table {
    @media (max-width: 410px) {
      overflow-x: scroll !important;
      margin-left: 125px !important;
      box-shadow: inset 4px 0 4px 0 rgba(0, 0, 0, 0.15);
      th {
        height: 60px !important;
      }
      th:first-child,
      td:first-child {
        position: absolute !important;
        min-width: 125px !important;
        left: 0 !important;
      }
      th:first-child {
        border-bottom: 1px solid colors.$n75 !important;
        white-space: break-spaces !important;
      }
      th:last-child,
      td:last-child {
        padding-right: 70px !important;
      }
      .white-gradient::after {
        content: '';
        display: block;
        position: absolute;
        top: 0;
        bottom: 0;
        right: 0;
        width: 100px;
        height: 100%;
        pointer-events: none;
        background: -moz-linear-gradient(left, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 1) 100%); /* FF3.6-15 */
        background: -webkit-linear-gradient(left, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 1) 100%); /* Chrome10-25,Safari5.1-6 */
        background: linear-gradient(to right, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 1) 100%); /* W3C, IE10+, FF16+, Chrome26+, Opera12+, Safari7+ */
        filter: progid:DXImageTransform.Microsoft.gradient(startColorstr='#00ffffff', endColorstr='#ffffff', GradientType=1); /* IE6-9 */
      }
    }
  }

  .table-itext .i-text-message {
    @media (max-width: 284px) {
      max-width: 175px !important;
    }
    @media (min-width: 285px) and (max-width: 300px) {
      max-width: 200px !important;
    }
    @media (min-width: 301px) and (max-width: 339px) {
      max-width: 220px !important;
    }
  }
}
</style>
